import { Broker } from '@axo/shared/data-access/types/broker';
import { Product } from '@axo/shared/data-access/types/loan_application';
import { DataSharingStatus, MarketCountryCode } from '@axo/shared/types';
import * as Sentry from '@sentry/browser';
import formFields from '../../../constants/formFields/formFields';
import type {
  TAllFormFields,
  TParentStep,
} from '../../../constants/formFields/formFields.d';
import { getFirstStepWithError } from './getFirstStepWithError';

export type TGetNumberOfCompletedSteps = {
  reloadedData?: Record<string, unknown>;
  marketCountry: MarketCountryCode;
  product?: Product;
  broker?: Broker;
  amountOfSteps?: number;
  dataSharingStatus?: DataSharingStatus;
};

const getFieldVisibility = ({
  parents,
  allFilledFields,
  reloadedData,
}: {
  parents?: TParentStep[];
  allFilledFields: string[];
  reloadedData: TGetNumberOfCompletedSteps['reloadedData'];
}) => {
  if (!parents || !parents.length) return true;

  return parents.every((parent) => {
    // Parent should be empty
    if (parent.values === undefined)
      return !allFilledFields.includes(parent.id);

    // Parent should have any value
    if (parent.values?.length === 0) return allFilledFields.includes(parent.id);

    // parent should contain the correct value
    const currentParentValue = reloadedData?.[parent.id] as never;

    // ts-ignore because we know that parent.values is an array
    return parent.values.includes(currentParentValue);
  });
};

const getLastCompletedStepIndex = ({
  countrySteps,
  allFilledFields,
  reloadedData,
  broker,
  dataSharingStatus,
}: {
  allFilledFields: string[];
  reloadedData: TGetNumberOfCompletedSteps['reloadedData'];
  countrySteps: TAllFormFields[];
  broker?: Broker;
  dataSharingStatus?: DataSharingStatus;
}) =>
  countrySteps.findIndex((step) =>
    step.some((field) => {
      const isFieldFilled = allFilledFields.includes(field.id);

      if (isFieldFilled || field.isOptional) return false;

      if (field.broker && field.broker !== broker) return false;

      if (
        field.dataSharingStatus &&
        field.dataSharingStatus !== dataSharingStatus
      ) {
        return false;
      }

      const isFieldVisible = getFieldVisibility({
        allFilledFields,
        reloadedData,
        parents: field.parents,
      });

      if (!isFieldVisible) return false;

      console.warn('Field is not filled: ', field.id);
      Sentry.captureEvent({
        message: `IssueTrack: Field is not filled`,
        extra: {
          fieldId: field.id,
        },
      });

      return true;
    })
  );

const getNumberOfCompletedSteps = ({
  reloadedData,
  marketCountry,
  product = Product.UnsecuredLoan,
  broker,
  amountOfSteps,
  dataSharingStatus,
}: TGetNumberOfCompletedSteps) => {
  if (!reloadedData || !Object.keys(reloadedData).length) return 0;

  const allFilledFields = Object.keys(reloadedData).filter(
    (key) =>
      reloadedData[key] !== undefined &&
      reloadedData[key] !== '' &&
      reloadedData[key] !== null
  );

  const countrySteps = formFields[marketCountry][product];

  if (countrySteps.length === 0) return 0;

  const completedSteps = getLastCompletedStepIndex({
    countrySteps,
    allFilledFields,
    reloadedData,
    broker,
    dataSharingStatus,
  });

  const firstStepWithError = getFirstStepWithError({
    marketCountry,
    product,
    countrySteps,
    reloadedData,
  });

  if (firstStepWithError > -1) return firstStepWithError;

  //If all steps are completed, return the last step index
  return completedSteps === -1
    ? amountOfSteps ?? countrySteps.length - 1
    : completedSteps;
};

export {
  getFieldVisibility,
  getLastCompletedStepIndex,
  getNumberOfCompletedSteps,
};
